"use client"

import { useState, useEffect } from "react"
import { FaStar, FaStarHalfAlt, FaCheck, FaTruck, FaPercent, FaGift, FaHeadset } from "react-icons/fa"
import { useParams } from "react-router-dom"
import { Toaster, toast } from "react-hot-toast"
import RichTextContent from "../../components/RichTextContent"
import { parseItemDetails } from "../../utils/itemDetails"
import { API_BASE } from "../../utils/apiBase"

export default function ProductDetailPage() {
  const { productId } = useParams()
  const [product, setProduct] = useState(null)
  const [error, setError] = useState(null)
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState(4000)
  const reviewsPerPage = 5

  useEffect(() => {
    const fetchDeliverySettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/v1/settings/delivery`)
        const result = await response.json()
        if (result.success && result.data?.freeDeliveryThreshold != null) {
          setFreeDeliveryThreshold(result.data.freeDeliveryThreshold)
        }
      } catch {
        // Keep default threshold
      }
    }
    fetchDeliverySettings()
  }, [])

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`https://api.alburaqscents.com/v1/product/${productId}`)
        const result = await response.json()
        if (result.success) {
          setProduct(result.data)
          toast.success("Product loaded successfully!")
        } else {
          toast.error("Failed to fetch product")
          setError("Failed to fetch product")
        }
      } catch (err) {
        toast.error(`Error: ${err.message}`)
        setError(err.message)
      }
    }

    fetchProduct()
  }, [productId])

  const styles = {
    container: {
      backgroundColor: "#E5D0A5",
      padding: "0",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      boxSizing: "border-box",
      width: "100%",
      overflowX: "hidden",
    },
    productDetailTitle: {
      color: "#000",
      fontSize: "clamp(24px, 5vw, 32px)", // Responsive font size
      fontWeight: "700",
      marginBottom: "35px",
      width: "100%",
      maxWidth: "1200px",
      textAlign: "left",
      boxSizing: "border-box",
      padding: "clamp(20px, 5vw, 45px) clamp(20px, 5vw, 40px) 0 clamp(20px, 5vw, 40px)",
      letterSpacing: "-0.5px",
      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
    },
    productDetailWrapper: {
      backgroundColor: "#000",
      borderRadius: "20px",
      padding: "clamp(20px, 5vw, 45px)",
      width: "calc(100% - clamp(40px, 10vw, 80px))",
      maxWidth: "1200px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr", // Simplified grid for larger screens
      gap: "clamp(20px, 5vw, 40px)",
      boxSizing: "border-box",
      marginBottom: "35px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      position: "relative",
      overflow: "hidden",
    },
    imageGallery: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
    },
    mainImageContainer: {
      borderRadius: "15px",
      overflow: "hidden",
      width: "100%", // Full width for responsiveness
      maxWidth: "400px", // Cap the width for larger screens
      aspectRatio: "2 / 3", // Maintain aspect ratio instead of fixed height
      border: "8px solid #D4AF37",
      backgroundColor: "#1E1E1E",
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      cursor: "pointer",
      position: "relative",
      boxShadow: "0 4px 15px rgba(0,0,0,0.5)",
    },
    mainImage: {
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "inherit",
      transition: "transform 0.3s ease",
    },
    thumbnailGallery: {
      display: "flex",
      gap: "10px",
      justifyContent: "center",
      width: "100%",
      flexWrap: "wrap",
    },
    thumbnailContainer: {
      borderRadius: "10px",
      overflow: "hidden",
      border: "5px solid #D4AF37",
      cursor: "pointer",
      width: "clamp(50px, 15vw, 70px)", // Responsive thumbnail size
      height: "clamp(50px, 15vw, 70px)",
      flexShrink: 0,
      backgroundColor: "#1E1E1E",
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "all 0.3s ease",
      position: "relative",
    },
    thumbnail: {
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: "inherit",
      transition: "opacity 0.3s ease",
    },
    productInfo: {
      color: "#FFF",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    productTitle: {
      fontSize: "clamp(24px, 5vw, 36px)", // Responsive font size
      fontWeight: "700",
      marginBottom: "8px",
      color: "#D4AF37",
      letterSpacing: "-0.5px",
      lineHeight: "1.2",
      textShadow: "0 2px 4px rgba(0,0,0,0.3)",
    },
    rating: {
      display: "flex",
      alignItems: "center",
      color: "#FFC107",
      fontSize: "clamp(14px, 3vw, 18px)",
      marginBottom: "18px",
      gap: "2px",
    },
    ratingCount: {
      color: "#DDD",
      fontSize: "clamp(12px, 2.5vw, 15px)",
      marginLeft: "12px",
      fontWeight: "500",
    },
    priceSection: {
      display: "flex",
      alignItems: "baseline",
      marginBottom: "25px",
      flexWrap: "wrap",
      gap: "8px",
    },
    currentPrice: {
      fontSize: "clamp(24px, 5vw, 32px)",
      fontWeight: "700",
      color: "#FFF",
      textShadow: "0 1px 2px rgba(0,0,0,0.5)",
    },
    oldPrice: {
      fontSize: "clamp(16px, 3vw, 20px)",
      color: "#AAA",
      textDecoration: "line-through",
      marginLeft: "15px",
      fontWeight: "500",
    },
    discountBadge: {
      backgroundColor: "#D4AF37",
      color: "#000",
      padding: "6px 14px",
      borderRadius: "8px",
      fontSize: "clamp(12px, 2.5vw, 14px)",
      fontWeight: "700",
      marginLeft: "15px",
      boxShadow: "0 2px 8px rgba(212, 175, 55, 0.3)",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    sizeOptions: {
      marginBottom: "25px",
    },
    sizeLabel: {
      color: "#DDD",
      fontWeight: "600",
      marginBottom: "12px",
      display: "block",
      fontSize: "clamp(14px, 3vw, 16px)",
      letterSpacing: "0.5px",
    },
    sizeButtons: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
    },
    sizeButton: {
      backgroundColor: "#1E1E1E",
      color: "#D4AF37",
      border: "2px solid #D4AF37",
      borderRadius: "10px",
      padding: "12px 24px",
      fontSize: "clamp(14px, 3vw, 16px)",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "600",
      letterSpacing: "0.5px",
      position: "relative",
      overflow: "hidden",
    },
    checkmarkItem: {
      display: "flex",
      alignItems: "center",
      color: "#FFF",
      fontSize: "clamp(14px, 3vw, 16px)",
      marginBottom: "5px",
      gap: "10px",
      padding: "4px 0",
    },
    checkmarkIcon: {
      color: "#A6F75A",
      fontSize: "clamp(14px, 3vw, 18px)",
      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
    },
    descriptionSection: {
      marginTop: "25px",
      padding: "20px 0",
      borderTop: "1px solid #333",
    },
    descriptionTitle: {
      color: "#FFF",
      fontWeight: "700",
      marginBottom: "12px",
      fontSize: "clamp(16px, 3vw, 18px)",
      letterSpacing: "0.5px",
    },
    descriptionText: {
      color: "#CCC",
      fontSize: "clamp(13px, 2.5vw, 15px)",
      lineHeight: "1.7",
      marginBottom: "15px",
    },
    offerText: {
      color: "#CCC",
      fontSize: "clamp(12px, 2.5vw, 14px)",
      lineHeight: "1.7",
      marginTop: "12px",
    },
    benefitsBar: {
      backgroundColor: "#000",
      color: "#FFF",
      padding: "clamp(15px, 5vw, 30px)",
      borderRadius: "15px",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", // Responsive grid
      gap: "25px",
      marginBottom: "35px",
      width: "calc(100% - clamp(40px, 10vw, 80px))",
      maxWidth: "1200px",
      boxSizing: "border-box",
      textAlign: "center",
      boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
    },
    benefitItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "12px",
      padding: "15px",
      borderRadius: "10px",
      transition: "transform 0.3s ease",
      cursor: "pointer",
    },
    benefitIcon: {
      fontSize: "clamp(24px, 5vw, 32px)",
      color: "#D4AF37",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
    },
    benefitTextBold: {
      fontWeight: "700",
      fontSize: "clamp(15px, 3vw, 17px)",
      letterSpacing: "0.5px",
    },
    benefitTextSmall: {
      fontSize: "clamp(11px, 2.5vw, 13px)",
      color: "#AAA",
      lineHeight: "1.4",
    },
    detailReviewWrapper: {
      width: "calc(100% - clamp(40px, 10vw, 80px))",
      maxWidth: "1200px",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", // Responsive grid
      gap: "35px",
      boxSizing: "border-box",
    },
    itemDetail: {
      backgroundColor: "#000",
      color: "#f1f1f1",
      borderRadius: "15px",
      padding: "clamp(15px, 5vw, 30px)",
      fontFamily: '"Segoe UI", sans-serif',
      lineHeight: "1.7",
      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
      border: "1px solid #222",
    },
    itemDetailTitle: {
      color: "#facc15",
      marginBottom: "20px",
      fontSize: "clamp(18px, 3vw, 22px)",
      fontWeight: "700",
      borderBottom: "2px solid #333",
      paddingBottom: "12px",
      letterSpacing: "0.5px",
    },
    reviews: {
      backgroundColor: "#000",
      borderRadius: "15px",
      padding: "clamp(15px, 5vw, 30px)",
      color: "#CCC",
      fontSize: "clamp(13px, 2.5vw, 15px)",
      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
      border: "1px solid #222",
    },
    reviewsTitle: {
      color: "#D4AF37",
      fontWeight: "700",
      fontSize: "clamp(18px, 3vw, 22px)",
      marginBottom: "20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "2px solid #333",
      paddingBottom: "12px",
      letterSpacing: "0.5px",
    },
    addCommentButton: {
      backgroundColor: "transparent",
      color: "#D4AF37",
      border: "2px solid #D4AF37",
      borderRadius: "8px",
      padding: "10px 18px",
      fontSize: "clamp(12px, 2.5vw, 14px)",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "600",
      letterSpacing: "0.5px",
    },
    reviewItem: {
      marginBottom: "25px",
      paddingBottom: "25px",
      borderBottom: "1px solid #333",
    },
    reviewerInfo: {
      display: "flex",
      alignItems: "center",
      marginBottom: "12px",
    },
    reviewerAvatar: {
      width: "clamp(35px, 10vw, 45px)",
      height: "clamp(35px, 10vw, 45px)",
      backgroundColor: "#555",
      borderRadius: "50%",
      marginRight: "12px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "#DDD",
      fontWeight: "700",
      fontSize: "clamp(14px, 3vw, 18px)",
      border: "2px solid #666",
    },
    reviewerName: {
      color: "#DDD",
      fontWeight: "600",
      fontSize: "clamp(14px, 3vw, 16px)",
    },
    reviewText: {
      lineHeight: "1.7",
      fontSize: "clamp(13px, 2.5vw, 15px)",
    },
    reviewImages: {
      display: "flex",
      gap: "10px",
      marginTop: "10px",
    },
    reviewImage: {
      width: "clamp(40px, 15vw, 60px)",
      height: "clamp(40px, 15vw, 60px)",
      objectFit: "cover",
      borderRadius: "8px",
      border: "2px solid #333",
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      gap: "10px",
      marginTop: "20px",
    },
    paginationButton: {
      backgroundColor: "#1E1E1E",
      color: "#D4AF37",
      border: "2px solid #D4AF37",
      borderRadius: "8px",
      padding: "8px 16px",
      fontSize: "clamp(12px, 2.5vw, 14px)",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontWeight: "600",
      letterSpacing: "0.5px",
    },
    activePage: {
      backgroundColor: "#D4AF37",
      color: "#000",
      boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)",
    },
    skeleton: {
      backgroundColor: "#222",
      animation: "pulse 1.5s infinite",
    },
    skeletonImage: {
      width: "100%",
      maxWidth: "400px",
      aspectRatio: "2 / 3",
      backgroundColor: "#222",
      animation: "pulse 1.5s infinite",
    },
    skeletonThumbnail: {
      width: "clamp(50px, 15vw, 70px)",
      height: "clamp(50px, 15vw, 70px)",
      backgroundColor: "#222",
      animation: "pulse 1.5s infinite",
    },
    skeletonText: {
      width: "100%",
      height: "20px",
      backgroundColor: "#222",
      marginBottom: "10px",
      animation: "pulse 1.5s infinite",
    },
    skeletonReview: {
      width: "100%",
      height: "100px",
      backgroundColor: "#222",
      marginBottom: "20px",
      animation: "pulse 1.5s infinite",
    },
  }

  const renderRating = (rating) => {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)
    const stars = []

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} />)
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" />)
    }
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} style={{ color: "#777" }} />)
    }
    return stars
  }

  if (error) {
    return <div style={styles.container}>Error: {error}</div>
  }

  if (!product) {
    return (
      <div style={styles.container}>
        <style>
          {`
            @keyframes pulse {
              0% { opacity: 1; }
              50% { opacity: 0.6; }
              100% { opacity: 1; }
            }
          `}
        </style>
        <Toaster position="top-center" reverseOrder={false} />
        <h2 style={styles.productDetailTitle}>Product Detail</h2>
        <div style={styles.productDetailWrapper}>
          <div style={styles.imageGallery}>
            <div style={styles.skeletonImage}></div>
            <div style={styles.thumbnailGallery}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} style={styles.skeletonThumbnail}></div>
              ))}
            </div>
          </div>
          <div style={styles.productInfo}>
            <div style={styles.skeletonText}></div>
            <div style={styles.skeletonText}></div>
            <div style={styles.skeletonText}></div>
            <div style={styles.skeletonText}></div>
            <div style={styles.skeletonText}></div>
            <div style={{ ...styles.skeletonText, height: "150px" }}></div>
          </div>
        </div>
        <div style={styles.benefitsBar}>
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} style={styles.skeleton}>
              <div style={styles.skeletonText}></div>
              <div style={styles.skeletonText}></div>
            </div>
          ))}
        </div>
        <div style={styles.detailReviewWrapper}>
          <div style={styles.itemDetail}>
            <div style={styles.skeletonText}></div>
            <div style={{ ...styles.skeletonText, height: "200px" }}></div>
          </div>
          <div style={styles.reviews}>
            <div style={styles.skeletonText}></div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} style={styles.skeletonReview}></div>
            ))}
            <div style={styles.skeletonText}></div>
          </div>
        </div>
      </div>
    )
  }

  const selectedSize = product.sizes[selectedSizeIndex]
  const currentPrice = selectedSize.price * (1 - selectedSize.discount / 100)
  const oldPrice = selectedSize.discount > 0 ? selectedSize.price : null
  const discount = selectedSize.discount > 0 ? `${selectedSize.discount}% off` : null
  const displaySizes = product.sizes.map((size) => `${size.ml}ml`)

  // Pagination logic
  const indexOfLastReview = currentPage * reviewsPerPage
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage
  const currentReviews = product.reviews ? product.reviews.slice(indexOfFirstReview, indexOfLastReview) : []
  const totalPages = Math.ceil((product.reviews?.length || 0) / reviewsPerPage)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.6; }
            100% { opacity: 1; }
          }
          
          .size-button:hover {
            background-color: #D4AF37 !important;
            color: #000 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
          }
          
          .benefit-item:hover {
            transform: translateY(-3px);
            background-color: #111;
          }
          
          .add-comment-button:hover {
            background-color: #D4AF37 !important;
            color: #000 !important;
            transform: translateY(-1px);
          }
          
          .main-image-container:hover {
            transform: scale(1.02);
            box-shadow: 0 8px 25px rgba(229, 208, 165, 0.3);
          }
          
          .thumbnail-container:hover {
            transform: scale(1.1);
            border-color: #FFD700 !important;
          }
          
          .pagination-button:hover {
            background-color: #D4AF37 !important;
            color: #000 !important;
            transform: translateY(-1px);
          }

          /* Media Queries for Responsiveness */
          @media (max-width: 1024px) {
            .product-detail-wrapper {
              grid-template-columns: 1fr; /* Stack image and info vertically */
              justify-items: center;
            }
            .main-image-container {
              max-width: 350px; /* Slightly smaller for tablets */
            }
            .thumbnail-container {
              width: clamp(40px, 12vw, 60px);
              height: clamp(40px, 12vw, 60px);
            }
            .product-info {
              align-items: center;
              text-align: center;
            }
            .size-buttons {
              justify-content: center;
            }
          }

          @media (max-width: 768px) {
            .product-detail-title {
              font-size: clamp(20px, 4vw, 28px);
              padding: clamp(15px, 4vw, 30px) clamp(15px, 4vw, 20px) 0 clamp(15px, 4vw, 20px);
            }
            .product-detail-wrapper {
              padding: clamp(15px, 4vw, 30px);
              width: calc(100% - clamp(20px, 8vw, 40px));
            }
            .main-image-container {
              max-width: 300px;
              aspect-ratio: 1 / 1.5; /* Adjust aspect ratio for smaller screens */
            }
            .thumbnail-container {
              width: clamp(35px, 10vw, 50px);
              height: clamp(35px, 10vw, 50px);
            }
            .product-title {
              font-size: clamp(20px, 4vw, 28px);
            }
            .current-price {
              font-size: clamp(20px, 4vw, 28px);
            }
            .old-price {
              font-size: clamp(14px, 3vw, 18px);
            }
            .discount-badge {
              font-size: clamp(10px, 2vw, 12px);
            }
            .benefits-bar {
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              padding: clamp(10px, 3vw, 20px);
              width: calc(100% - clamp(20px, 8vw, 40px));
            }
            .detail-review-wrapper {
              grid-template-columns: 1fr; /* Stack details and reviews vertically */
              width: calc(100% - clamp(20px, 8vw, 40px));
            }
          }

          @media (max-width: 480px) {
            .product-detail-title {
              font-size: clamp(18px, 5vw, 24px);
            }
            .product-detail-wrapper {
              padding: clamp(10px, 3vw, 20px);
            }
            .main-image-container {
              max-width: 100%;
              aspect-ratio: 1 / 1.2;
            }
            .thumbnail-container {
              width: clamp(30px, 8vw, 40px);
              height: clamp(30px, 8vw, 40px);
            }
            .product-title {
              font-size: clamp(18px, 4vw, 24px);
            }
            .current-price {
              font-size: clamp(18px, 4vw, 24px);
            }
            .size-button {
              padding: 8px 16px;
              font-size: clamp(12px, 3vw, 14px);
            }
            .benefits-bar {
              grid-template-columns: 1fr; /* Stack benefits vertically */
            }
            .benefit-item {
              padding: 10px;
            }
            .benefit-icon {
              font-size: clamp(20px, 4vw, 24px);
            }
            .benefit-text-bold {
              font-size: clamp(13px, 3vw, 15px);
            }
            .benefit-text-small {
              font-size: clamp(10px, 2vw, 12px);
            }
            .reviewer-avatar {
              width: clamp(30px, 8vw, 40px);
              height: clamp(30px, 8vw, 40px);
              font-size: clamp(12px, 3vw, 16px);
            }
            .review-image {
              width: clamp(30px, 8vw, 40px);
              height: clamp(30px, 8vw, 40px);
            }
          }
        `}
      </style>
      <Toaster position="top-center" reverseOrder={false} />

      <h2 style={styles.productDetailTitle}>Product Detail</h2>

      <div style={styles.productDetailWrapper} className="product-detail-wrapper">
        <div style={styles.imageGallery}>
          <div className="main-image-container" style={styles.mainImageContainer}>
            <img src={product.images[selectedImageIndex]} alt={product.name} style={styles.mainImage} />
          </div>
          <div style={styles.thumbnailGallery}>
            {product.images.map((image, index) => (
              <div
                key={index}
                className="thumbnail-container"
                style={{
                  ...styles.thumbnailContainer,
                  border: selectedImageIndex === index ? "5px solid #FFD700" : "5px solid #D4AF37",
                }}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img src={image} alt={`${product.name} - ${index + 1}`} style={styles.thumbnail} />
              </div>
            ))}
          </div>
        </div>

        <div style={styles.productInfo} className="product-info">
          <h1 style={styles.productTitle} className="product-title">{product.name}</h1>
          <div style={styles.rating}>
            {renderRating(product.rating)}
            <span style={styles.ratingCount}>({product.likes + product.dislikes} reviews)</span>
          </div>
          <div style={styles.priceSection}>
            <span style={styles.currentPrice} className="current-price">Rs.{currentPrice.toLocaleString()}</span>
            {oldPrice && <span style={styles.oldPrice} className="old-price">Rs.{oldPrice.toLocaleString()}</span>}
            {discount && <span style={styles.discountBadge} className="discount-badge">{discount}</span>}
          </div>
          <div style={styles.sizeOptions}>
            <label style={styles.sizeLabel}>Size</label>
            <div style={styles.sizeButtons} className="size-buttons">
              {displaySizes.map((size, index) => (
                <button
                  key={size}
                  className="size-button"
                  style={{
                    ...styles.sizeButton,
                    ...(selectedSizeIndex === index
                      ? {
                          backgroundColor: "#D4AF37",
                          color: "#000",
                          boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)",
                        }
                      : {}),
                  }}
                  onClick={() => setSelectedSizeIndex(index)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          <div style={styles.checkmarkItem}>
            <FaCheck style={styles.checkmarkIcon} />
            {selectedSize.stock > 0 ? "In Stock" : "Out of Stock"}
          </div>
          <div style={styles.checkmarkItem}>
            <FaCheck style={styles.checkmarkIcon} /> Free delivery available
          </div>
          <div style={styles.checkmarkItem}>
            <FaCheck style={styles.checkmarkIcon} /> Sales 10% Off Use Code: CODE123
          </div>
          <div style={styles.descriptionSection}>
            <h3 style={styles.descriptionTitle}>Description :</h3>
            <RichTextContent content={product.description} style={styles.descriptionText} />
            <p style={styles.offerText}>
              **Available offers :**
              <br />
              Bank Offer 10% instant discount on Bank Debit Cards, up to $30 on orders of $50 and above
              <br />
              Bank Offer Grab our exclusive offer now and save 20% on your next purchase!
              <br />
              Don't miss out, shop today!
            </p>
          </div>
        </div>
      </div>

      <div style={styles.benefitsBar} className="benefits-bar">
        <div className="benefit-item" style={styles.benefitItem}>
          <div style={styles.benefitIcon} className="benefit-icon">
            <FaTruck />
          </div>
          <div style={styles.benefitTextBold} className="benefit-text-bold">Free shipping</div>
          <div style={styles.benefitTextSmall} className="benefit-text-small">for orders of Rs {Number(freeDeliveryThreshold).toLocaleString('en-PK')} or more</div>
        </div>
        <div className="benefit-item" style={styles.benefitItem}>
          <div style={styles.benefitIcon} className="benefit-icon">
            <FaPercent />
          </div>
          <div style={styles.benefitTextBold} className="benefit-text-bold">Special discount</div>
          <div style={styles.benefitTextSmall} className="benefit-text-small">affiliate discount up to 10%</div>
        </div>
        <div className="benefit-item" style={styles.benefitItem}>
          <div style={styles.benefitIcon} className="benefit-icon">
            <FaGift />
          </div>
          <div style={styles.benefitTextBold} className="benefit-text-bold">Free gift wrapping</div>
          <div style={styles.benefitTextSmall} className="benefit-text-small">With hand written custom note</div>
        </div>
        <div className="benefit-item" style={styles.benefitItem}>
          <div style={styles.benefitIcon} className="benefit-icon">
            <FaHeadset />
          </div>
          <div style={styles.benefitTextBold} className="benefit-text-bold">Expert customer service</div>
          <div style={styles.benefitTextSmall} className="benefit-text-small">Open 9:00-20:00 7 days</div>
        </div>
      </div>

      <div style={styles.detailReviewWrapper} className="detail-review-wrapper">
        <div style={styles.itemDetail}>
          <h2 style={styles.itemDetailTitle}>Item Details</h2>
          <ul style={{ listStyleType: "disc", paddingLeft: "20px", margin: 0 }}>
            {parseItemDetails(product?.itemDetails).map((detail, index) => (
              <li key={index} style={{ marginBottom: "12px", fontSize: "clamp(13px, 2.5vw, 15px)" }}>
                {detail}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.reviews}>
          <div style={styles.reviewsTitle}>
            <h2>Reviews</h2>
            <button className="add-comment-button" style={styles.addCommentButton}>
              Add comment
            </button>
          </div>
          {product.reviews && product.reviews.length > 0 ? (
            <>
              {currentReviews.map((review, index) => (
                <div key={index} style={styles.reviewItem}>
                  <div style={styles.reviewerInfo}>
                    <div style={styles.reviewerAvatar} className="reviewer-avatar">
                      {review.user?.name ? review.user.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <span style={styles.reviewerName} className="reviewer-name">
                      {review.user?.name || "Anonymous"}
                    </span>
                  </div>
                  <div style={styles.rating}>{renderRating(review.rating)}</div>
                  <p style={styles.reviewText}>{review.comment || "No comment provided"}</p>
                  {review.images && review.images.length > 0 && (
                    <div style={styles.reviewImages}>
                      {review.images.map((image, imgIndex) => (
                        <img
                          key={imgIndex}
                          src={image}
                          alt={`Review image ${imgIndex + 1}`}
                          style={styles.reviewImage}
                          className="review-image"
                        />
                      ))}
                    </div>
                  )}
                  <p style={{ ...styles.reviewText, fontSize: "clamp(11px, 2vw, 13px)", color: "#AAA" }}>
                    Reviewed on {new Date(review.createdAt).toLocaleDateString()}
                  </p>
                </div>
              ))}
              <div style={styles.pagination}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    style={{
                      ...styles.paginationButton,
                      ...(currentPage === page ? styles.activePage : {}),
                    }}
                    className="pagination-button"
                    onClick={() => paginate(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p style={styles.reviewText}>No reviews yet.</p>
          )}
        </div>
      </div>
    </div>
  )
}